import { isViewable } from '../../../types';
const ownerNames = (property) => property.owners.filter(o => o.length > 0).join(', ');
// The contributor a view is presented as: its first holder, or a neutral label for a view with no user_parcels row.
export const attributionLabel = (property) => { var _a; return (_a = property.owners.find(o => o.length > 0)) !== null && _a !== void 0 ? _a : 'Another contributor'; };
// Row-level author when the row has one; otherwise the owning property's owner names.
const attributionFor = (property, addedByName) => addedByName.length > 0 ? addedByName : ownerNames(property);
const byUpdatedDesc = (a, b) => b.updated.compareTo(a.updated);
const contributionsFrom = (view, myId, rows, addedByName) => view.properties.flatMap(p => rows(p).map(row => ({
    row,
    'property': p,
    'canEdit': p.id === myId,
    'attribution': attributionFor(p, addedByName(row)),
})));
export const mergedTimeline = (view, myId) => contributionsFrom(view, myId, p => p.timeline, t => t.addedByName);
export const mergedResidents = (view, myId) => contributionsFrom(view, myId, p => p.residents, () => '');
export const mergedPhotos = (view, myId) => contributionsFrom(view, myId, p => p.assets.photos, photo => photo.addedByName);
// The published views on a parcel, newest first.
export const publicViews = (view) => view.properties.filter(isViewable).sort(byUpdatedDesc);
// The parcel narrowed to its published views, for the read-only page to merge from.
export const publicParcelView = (view) => (Object.assign(Object.assign({}, view), { 'properties': publicViews(view) }));
// Own views, then organization contributions the viewer may edit (public or not), then other public views; each newest first.
export const stories = (view) => {
    const isOwn = (p) => view.ownPropertyIds.includes(p.id);
    const isOrg = (p) => view.orgProperties.some(o => o.propertyId === p.id);
    const isEditableOrg = (p) => isOrg(p) && view.editablePropertyIds.includes(p.id);
    const story = (p, own, isPublic) => ({
        'property': p,
        'isOwn': own,
        'isPublic': isPublic,
        'isOrg': isOrg(p),
        'orgStories': view.orgStories.filter(s => s.propertyId === p.id),
    });
    const own = view.properties
        .filter(isOwn)
        .sort(byUpdatedDesc)
        .map(p => story(p, true, isViewable(p)));
    const editableOrg = view.properties
        .filter(p => !isOwn(p) && isEditableOrg(p))
        .sort(byUpdatedDesc)
        .map(p => story(p, false, isViewable(p)));
    const others = publicViews(view)
        .filter(p => !isOwn(p) && !isEditableOrg(p))
        .map(p => story(p, false, true));
    return [...own, ...editableOrg, ...others];
};
// The parcel as one organization view shows it: its contribution leads, public views follow, each narrowed to the selected rows.
export const orgStoryLens = (view, story) => {
    const selected = (rows, ids) => rows.filter(r => ids.includes(r.id));
    const leads = (p) => p.id === story.propertyId;
    const properties = view.properties
        .filter(p => leads(p) || isViewable(p))
        .sort((a, b) => leads(a) ? -1 : leads(b) ? 1 : byUpdatedDesc(a, b))
        .map((p) => (Object.assign(Object.assign({}, p), { 'timeline': selected(p.timeline, story.timelineIds), 'residents': selected(p.residents, story.residentIds), 'assets': Object.assign(Object.assign({}, p.assets), { 'photos': p.assets.photos.filter(photo => { var _a; return story.assetIds.includes((_a = photo.assetId) !== null && _a !== void 0 ? _a : ''); }) }) })));
    return Object.assign(Object.assign({}, view), { 'properties': properties });
};
const sameAsset = (a, b) => !!a && a === b;
// The image an organization view's hero shows: its chosen header, else the contribution's own header when it is in the view, else the view's first photo.
export const orgStoryHeroUrl = (lens, story) => {
    var _a, _b, _c, _d;
    const photos = lens.properties.flatMap(p => p.assets.photos.map(photo => ({ photo, 'variants': p.assets.headerVariants })));
    const lead = lens.properties[0];
    const chosen = (_c = (_b = (_a = photos.find(x => sameAsset(x.photo.assetId, story.headerAssetId))) !== null && _a !== void 0 ? _a : photos.find(x => { var _a; return sameAsset(x.photo.assetId, (_a = lead === null || lead === void 0 ? void 0 : lead.assets.thumbnailPhoto) === null || _a === void 0 ? void 0 : _a.assetId); })) !== null && _b !== void 0 ? _b : photos[0]) !== null && _c !== void 0 ? _c : null;
    if (chosen === null)
        return null;
    // The property page shows a photo's header crop when one exists; the view does the same.
    return ((_d = chosen.variants.find(v => sameAsset(v.assetId, chosen.photo.assetId))) !== null && _d !== void 0 ? _d : chosen.photo).url;
};
// Non-empty values of one scalar field from every property but mine, newest first.
export const otherValues = (view, myId, get) => [...view.properties]
    .filter(p => p.id !== myId)
    .sort(byUpdatedDesc)
    .map(p => ({ 'value': get(p).trim(), 'attribution': ownerNames(p) }))
    .filter(x => x.value.length > 0);
const hasText = (x) => x.trim().length > 0;
// Facts that have a value.
export const nonEmptyQuickfacts = (facts) => facts.filter(x => hasText(x.value));
// Whether a story card would render anything; Owner's Details counts only on the viewer's own.
export const storyHasContent = (story) => {
    const p = story.property;
    return hasText(p.title)
        || hasText(p.established)
        || hasText(p.overview)
        || nonEmptyQuickfacts(p.quickfacts).length > 0
        || (story.isOwn && hasText(p.ownerDetails));
};
// The community's most recently updated published view of the parcel, if it has one.
export const communityStory = (view, communityId) => {
    var _a;
    return (_a = view.orgStories
        .filter(s => s.community.id === communityId && s.status === 'published')
        .sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime())[0]) !== null && _a !== void 0 ? _a : null;
};
